// The previous value of a changed field, as the clinician reads it: "Was: …"
// (TGCLOUD-1902, rule 4). The value is a record's DATA, so it is never put
// through t() itself; the caller puts it into a sentence with a placeholder,
// t('Was: {{value}}', { value }), which keeps it out of the translation cache.
// The words this adds of its own (Yes, No, an option's label) are UI text and
// are translated.

import { toLocalDateFromPickerOrImport } from 'src/util/partialDate';
import { isBlankValue, isDateLike, toFlag } from './compareFieldValues';

const READABLE_KEYS = ['label', 'name', 'title', 'displayName', 'text'];

// A long note is cut short in the tooltip; the field itself holds the rest.
export const MAX_SHOWN_LENGTH = 160;

const clip = (text) =>
  text.length > MAX_SHOWN_LENGTH
    ? `${text.slice(0, MAX_SHOWN_LENGTH - 1).trimEnd()}…`
    : text;

const pad = (n) => String(n).padStart(2, '0');

const optionValue = (option) =>
  option && typeof option === 'object' ? option.value : option;

const optionLabel = (option) =>
  option && typeof option === 'object'
    ? option.label ?? option.name ?? String(option.value ?? '')
    : String(option);

const sameOption = (option, value) => {
  const v = optionValue(option);
  if (v === value) return true;
  if (v === null || v === undefined || value === null || value === undefined)
    return false;
  return String(v) === String(value);
};

/**
 * How a saved value reads in "Was: …".
 *
 * @param {*} value  the value as last saved
 * @param {object} [helpers]
 * @param {Function} [helpers.t]  the component's translator (Yes, No, option labels)
 * @param {Function} [helpers.fmtDate]  useDateFormatter's formatter
 * @param {Array} [helpers.options]  the field's options: a value is shown as its label
 * @param {boolean} [helpers.translateOptions]  false for options that are data (people, clinics)
 * @param {string} [helpers.kind]  as compareFieldValues takes it ('flag', 'date', 'datetime')
 * @returns {string|null|undefined} null for no value; undefined when the value
 *   has no readable form (an object with nothing to show)
 */
export function formatFieldValue(
  value,
  { t = (s) => s, fmtDate, options, translateOptions = true, kind } = {}
) {
  if (kind === 'flag' || typeof value === 'boolean')
    return toFlag(value) ? t('Yes') : t('No');
  if (isBlankValue(value)) return null;

  if (Array.isArray(value)) {
    const parts = value
      .map((item) =>
        formatFieldValue(item, { t, fmtDate, options, translateOptions })
      )
      .filter((part) => typeof part === 'string' && part !== '');
    return parts.length ? clip(parts.join(', ')) : null;
  }

  if (Array.isArray(options)) {
    const hit = options.find((option) => sameOption(option, value));
    if (hit) {
      const label = optionLabel(hit);
      return clip(translateOptions ? t(label) : label);
    }
  }

  if (kind === 'date' || kind === 'datetime' || isDateLike(value)) {
    const d = toLocalDateFromPickerOrImport(
      typeof value === 'string' ? value.trim() : value
    );
    if (d && !Number.isNaN(d.getTime())) {
      const day = fmtDate ? fmtDate(d) : d.toDateString();
      return kind === 'datetime'
        ? `${day} ${pad(d.getHours())}:${pad(d.getMinutes())}`
        : day;
    }
  }

  if (typeof value === 'object') {
    for (const key of READABLE_KEYS) {
      const v = value[key];
      if (typeof v === 'string' && v.trim() !== '') return clip(v.trim());
      if (typeof v === 'number') return String(v);
    }
    const person = [value.firstName, value.lastName]
      .filter((part) => typeof part === 'string' && part.trim() !== '')
      .join(' ');
    if (person) return clip(person);
    if (value.value !== undefined && typeof value.value !== 'object')
      return clip(String(value.value));
    return undefined;
  }

  return clip(typeof value === 'string' ? value.trim() : String(value));
}

export default formatFieldValue;
